import type { FC } from 'react';
import { Alert, Button, Container, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormatDate } from '@pflegenavi/frontend/localization';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';
import type { LegalEntityKycWarning } from './legalEntityKycWarnings';

interface LegalEntityKycBannerViewProps {
  warnings: LegalEntityKycWarning[];
  onOpenDialog: (legalEntityId: string) => void;
}

export const LegalEntityKycBannerView: FC<LegalEntityKycBannerViewProps> = ({
  warnings,
  onOpenDialog,
}) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();

  if (warnings.length === 0) {
    return null;
  }

  return (
    <Container sx={{ maxWidth: 1400, mb: 3 }}>
      <Stack
        sx={{
          gap: 1,
        }}
      >
        {warnings.map(({ legalEntity, warning }) => (
          <Alert
            key={legalEntity.id}
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => onOpenDialog(legalEntity.id)}
              >
                {warning === 'renewal_due'
                  ? t('kyc.idv.renew-button')
                  : t('kyc.idv.verify-button')}
              </Button>
            }
          >
            {warning === 'renewal_due' && legalEntity.kyc_renewal_deadline
              ? t('kyc.idv.banner.legal-entity-renewal-due', {
                  name: legalEntity.base_data.company,
                  date: formatDate(
                    new Date(legalEntity.kyc_renewal_deadline),
                    YEAR_MONTH_DAY_SHORT_FORMAT
                  ),
                })
              : t('kyc.idv.banner.legal-entity-not-verified', {
                  name: legalEntity.base_data.company,
                })}
          </Alert>
        ))}
      </Stack>
    </Container>
  );
};
